import { C_OUTLINE_VARIANT } from "@schaeffler-commerce/design-system";
import { GlobalStyles as MuiGlobalStyles, type Theme } from "@schaeffler-commerce/design-system";

const MATERIAL_WARNING_ICON_MASK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'/%3E%3C/svg%3E") center / contain no-repeat`;

export const externalContentStyles = (theme: Theme) => ({
  ".scha__lc__external-label": {
    "&.scha__bold-b-gone b, &.scha__bold-b-gone strong": {
      fontWeight: "normal",
    },
    "& p, & h6, & h5, & h4, & h3": {
      marginBottom: "5px",
      fontSize: "inherit",
      lineHeight: "inherit",
      "&:first-child": {
        marginTop: 0,
      },
      "&:last-child": {
        marginBottom: 0,
      },
    },
  },
  ".scha__lc__external-content--shaft": {
    "& p": {
      ...theme.typography.bodyMedium,
    },
    "& h2, & h3, & h4, & h5, & h6, & p.heading_6, & span.heading3": {
      ...theme.typography.titleMedium,
      marginTop: 0,
    },
    "& h2": {
      fontWeight: "bold",
    },
    "& *:not(.attention):not(.achtung)": {
      "& h3, & h4, & h5, & h6, & p.heading_6, & span.heading3": {
        fontWeight: "bold",
      },
    },
    "& span.heading3": {
      display: "block",
    },
    "& h2, & h3, & h4, & h5, & h6, & p, & .achtung, & .attention, & ul, & span.heading3, & div.figure, & figure": {
      marginBottom: 0,
      "&:not(:last-child)": {
        marginBottom: "8px",
      },
    },
    "& .unterkapitel\\.beschreibung + .unterkapitel\\.beschreibung": {
      marginTop: "32px",
    },
    "& p, & table, & .achtung, & .attention, & ul, & div.figure, & figure": {
      "& + h2, & + h3, & + h4, & + h5, & + h6, & + p.heading_6, & + span.heading3, & + div.figure, & + figure, & + .abschnitt\\.verweisextern": {
        marginTop: "32px",
      },
    },
    "& table": {
      width: "100%",
      "& + .achtung, & + .attention": {
        marginTop: "8px",
      },
      "& + table": {
        marginTop: "32px",
      },
      "& + table.tabletype-framed": {
        marginTop: "16px",
      },
      "& p &:not(:first-child)": {
        marginTop: "24px",
      },
      "& > caption": {
        ...theme.typography.titleMedium,
        textAlign: "left",
        fontWeight: "bold",
      },
      "& thead": {
        borderBottom: `3px solid ${C_OUTLINE_VARIANT}`,
      },
      "& thead > tr > th, & tbody > tr > td": {
        padding: "8px",
      },
      "& tbody tr:nth-child(even)": {
        background: theme.palette.surface.main,
      },
      "& .table_equation": {
        maxHeight: "250px",
      },
    },
    "& table.tabletype-legend": {
      "& > tbody > tr": {
        background: "unset",
        "& > td": {
          paddingBlock: 0,
        },
      },
    },
    "& table.tabletype-framed": {
      width: "max-content",
      whiteSpace: "nowrap",
      "& tbody tr": {
        background: "unset",
      },
      "& tbody td": {
        paddingTop: "2px",
        paddingBottom: "2px",
      },
    },
    "& .footnote-container": {
      marginTop: "8px",
      "& .footnote-number": {
        position: "relative",
        top: "-4px",
        marginRight: "-2px",
      },
    },
    "& p.fussnote\\.nr": {
      "&:not(:only-child)": {
        marginTop: "20px",
      },
      "& + &": {
        marginTop: "-15px",
      },
    },
    "& .achtung, & .attention": {
      color: theme.palette.info.main,
      paddingLeft: "23px",
      position: "relative",
      display: "block",
      "&:before": {
        content: '""',
        width: "15px",
        height: "15px",
        backgroundColor: "currentColor",
        mask: MATERIAL_WARNING_ICON_MASK,
        WebkitMask: MATERIAL_WARNING_ICON_MASK,
        position: "absolute",
        top: 0,
        left: 0,
      },
      "& > .signalwort": {
        display: "none",
      },
      "& > p": {
        ...theme.typography.bodyMedium,
        "& a": {
          color: "inherit",
          textDecoration: "underline",
        },
      },
    },
    "& .heading_list": {
      ...theme.typography.bodyMedium,
      fontWeight: "bold",
    },
    "& .abschnitt, & .legend_figure": {
      display: "flex",
      alignItems: "center",
      marginBottom: "10px",
    },
    "& div.figure, & figure": {
      color: theme.palette.text.secondary,
      "& .heading_figure": {
        color: theme.palette.text.primary,
      },
      "& p, & .abschnitt, & .legend_figure": {
        "&:first-child": {
          marginTop: "10px",
        },
      },
      "& > img": {
        maxHeight: "400px",
      },
    },
    "& figure": {
      display: "inline",
      marginRight: "5px",
    },
    "& figure > img": {
      height: "unset",
    },
    "& figure:not(.img_original) > img": {
      height: "18px",
    },
    "& table a > sup, & .fussnote\\.nr > sup, & .footnote-link": {
      ...theme.typography.bodyMedium,
      marginLeft: "-2px",
      position: "relative",
      top: "-4px",
    },
    "& .fm\\.formel\\.anker > img": {
      height: "4em",
      marginBottom: "10px",
    },
    "& figure + p": {
      marginTop: "32px",
    },
  },
  ".scha__lc__external-content--shaft.scha__lc__kits-table": {
    "& th, & td": {
      border: `1px solid ${C_OUTLINE_VARIANT}`,
    },
    "& thead": {
      border: "none !important",
    },
    "& tr": {
      background: "none !important",
    },
    "& table": {
      "& .table_figure": {
        width: "250px",
        maxWidth: "250px",
        minWidth: "250px",
      },
      "& .table_li": {
        "& > figure": {
          flex: "none",
          display: "inline",
        },
        "&:not(:first-child)": {
          marginTop: "10px",
        },
      },
    },
  },
  ".scha__lc__external-content--shaft.scha__lc__grease-dropdown": {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  ".MuiSelect-select .scha__lc__external-label > *": {
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  ".scha__modal.scha__lc__external-content--shaft__modal": {
    [theme.breakpoints.up("desktop-s")]: {
      width: "900px",
    },
  },
});

export const ExternalContentGlobalStyles = () => <MuiGlobalStyles styles={externalContentStyles} />;
